import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import TextField from '@Components/Shared/Controls/TextField';
import Toggle from '@Components/Shared/Controls/Toggle';
import SortDropdown from '@Components/Shared/SortDropdown';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useState, useEffect } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { sortDistanceWithEnteredLocation, runSortWithCurrentLocation } from '@Components/Utilities/DistanceSortHelper';
import { unsetScrollPosition, gtmPushEvent } from '@Components/Utilities/SandhillsUtility';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { hasCurrentUserLocationCookie } from '@Components/Utilities/CookieHelper';
import ListingOptions from '@Components/Shared/ListingOptions';
import { gtmEventType } from '@Components/Utilities/SandhillsConstants';

const ShowClosestFirstToggle = (props) => {
    const [showDistanceError, setShowDistanceError] = useState(props.ShowDistanceSortError);
    const [userEnteredLocation, setUserEnteredLocation] = useState(props.CurrentUserFormattedAddress);
    const routes = useRecoilValue(atoms.routes);
    const windowSize = useWindowSize();
    const [currentUser, setCurrentUser] = useRecoilState(atoms.currentUser);
    const currentSite = useRecoilValue(atoms.currentWebsite);

    const toggleChange = (isChecked) => {
        if (showDistanceError) {
            setUserEnteredLocation((props.CurrentUserFormattedAddress && isChecked) ? props.CurrentUserFormattedAddress : "");
        }
        if (isChecked) {
            gtmPushEvent(gtmEventType.scfToggle);
            // Immediately trigger browser geolocation prompt (runs in parallel with live event retrieval)
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(() => { }, () => { });
            }

            props.onSortChange(props.distanceSortID);
        }
        else {
            props.onSortChange(props.defaultSortID);
        }
    };

    useEffect(function updateCurrentLocation() {
        if (typeof props.CurrentUserFormattedAddress !== 'undefined' && props.CurrentUserFormattedAddress != userEnteredLocation) {
            setUserEnteredLocation(props.CurrentUserFormattedAddress);
        }
        else if (props.DistanceSortIsSelected && !userEnteredLocation) {
            toggleChange(false);
        }
    }, [props.CurrentUserFormattedAddress]);

    useEffect(function updateSortError() {
        setShowDistanceError(false);
    }, [props.DistanceSortIsSelected]);

    useEffect(function requestLocationOnMount() {
        // Ensure that we turn SCF on page load if a user lands on the Auctions Near Me page without location permissions
        if (props.requestLocationOnMount && !hasCurrentUserLocationCookie()) {
            toggleChange(true);
        }
    }, []);

    const sortWithCurrentLocation = () => {
        if (props.setShowDistanceModal && !hasCurrentUserLocationCookie()) {
            props.setShowDistanceModal(true)
        } else {
            runSortWithCurrentLocation(
                routes.FormattedLocationStringFromCurrentCoordinates,
                routes.AjaxGetCoordinatesFromLocationStringUrl,
                props.CurrentAuthenticatedUserLocationForCoordinates,
                routes.AjaxSetCurrentUserLocationCookie,
                routes.AjaxGetDecryptedCoordinatesFromCurrentLocationCookie
            ).then(location => {
                unsetScrollPosition();
                setShowDistanceError(false);
                props.OnUpdate({
                    Latitude: location.latitude,
                    Longitude: location.longitude,
                    UseCurrentLocation: true
                });
                setCurrentUser(user => {
                    return {
                        ...user,
                        CurrentLocation: {
                            ...user.CurrentLocation,
                            FormattedAddress: location.formattedAddress
                        }
                    };
                });
            }).catch(() => {
                setShowDistanceError(true);
            });
        }
    };

    const sortWithEnteredLocation = (e, enteredLocation) => {
        sortDistanceWithEnteredLocation(routes.AjaxGetCoordinatesFromLocationStringUrl, (enteredLocation || userEnteredLocation))
            .then(location => {
                unsetScrollPosition();
                setShowDistanceError(false);
                props.OnUpdate({
                    Latitude: location.latitude,
                    Longitude: location.longitude
                });
                setCurrentUser(user => {
                    return {
                        ...user,
                        CurrentLocation: {
                            ...user.CurrentLocation,
                            FormattedAddress: location.formattedAddress
                        }
                    };
                });
            })
            .catch(() => {
                setShowDistanceError(true);
            });
    };

    const getAutoCompleteCallback = () => {
        return (e, ui) => {
            setUserEnteredLocation(ui.item.value);
            sortWithEnteredLocation(e, ui.item.value);
        }
    };

    const getAutoCompleteUrl = (e) => {
        return routes.LocationAutosuggestUrl + encodeURI(e.target.value.trim());
    };

    const onKeyPress = (e) => {
        if (e.code === 'Enter') {
            sortWithEnteredLocation(e);
        }
    };

    const onFieldBlur = (e) => {
        if (props.CurrentUserFormattedAddress != userEnteredLocation) {
            if (!userEnteredLocation) {
                setUserEnteredLocation(props.CurrentUserFormattedAddress);
            }
            else {
                sortWithEnteredLocation(e);
            }
        }
    };

    const handleFieldChange = (e) => {
        setUserEnteredLocation(e.target.value);
    };

    return (
        <div className='show-closest-first-container'>
            <div className={`show-closest-first${props.displaySCFInput ? ' with-textbox' : ''}`}>
                    <div className='left'>
                        {showDistanceError &&
                            <div className='error'>
                                <p><TranslationText tKey='sandhills.wecouldnotretrieveyourlocation' /></p>
                            </div>}
                        <Toggle
                            classNames='closest-toggle'
                            id={'show-closest-first-toggle'}
                            IsChecked={props.DistanceSortIsSelected}
                            onChange={toggleChange}
                            label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.showclosestfirst' />}
                        />
                        {props.displaySCFInput &&
                            <div className='text-box-with-built-in-button'>
                                <label htmlFor='sort-location' className='visually-hidden'><TranslationText tKey='sandhills.citystatezip' /></label>
                                <TextField
                                    name='sort-location-autosuggest'
                                    value={userEnteredLocation}
                                    type='text'
                                    required={false}
                                    id='sort-location'
                                    onFieldChanged={handleFieldChange}
                                    updateField={setUserEnteredLocation}
                                    focus={false}
                                    placeholder={translator.translate('sandhills.citystatezip')}
                                    autoComplete={{
                                        ajaxUrl: (e) => getAutoCompleteUrl(e),
                                        callback: getAutoCompleteCallback()
                                    }}
                                    autoCompleteDelay='1'
                                    autoSuggestWidth={330}
                                    onKeyPress={onKeyPress}
                                    onFieldBlur={onFieldBlur}
                                />

                                {props.DisplayShowClosestFirstByConsent &&
                                    <button className='current-loc-btn ts-button' aria-label='Use My Current Location' onClick={sortWithCurrentLocation}>
                                            <i className='fas fa-crosshairs' aria-hidden='true'></i>
                                    </button>
                                }
                            </div>}
                    </div>
                <div className='right'>
                    {props.IsListingsPage && !props.IsEventCatalog && ((!(props.AuctionResultsPage || props.IsDismantledSearch) || currentSite.Site.IsBidcaller || currentSite.Site.IsAuctionTime) || ((props.IsWatchListMyBids || props.IsWatchList) && (currentSite.Site.IsAuctionSite || currentSite.Site.IsEquineMarket || currentSite.Site.IsLivestockMarket))) && props.ListingsToggles.length > 0 &&
                        <div className={'fti-toggle'}>
                            {props.ListingsToggles.map((toggle, idx) => {
                                return (
                                    <div
                                        id={toggle.Id}
                                        key={toggle.Id + idx}
                                        role='button'
                                        tabIndex='0'
                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); props.toggleFTISearch(toggle.Type, toggle.Criteria); } }}
                                        className={"sort " +
                                            (((toggle.IsActiveOnLoad && !props.IsRentalSearch && !props.IsFractionalSearch && !props.IsFTISearch && !props.IsOnlineAuctionSearch && !props.IsOnSiteAuctionSearch)
                                                || (toggle.Type == "FRACTIONAL" && props.IsFractionalSearch)
                                                || (toggle.Type == "RENT" && props.IsRentalSearch)
                                                || (toggle.Type == "FTI" && props.IsFTISearch)
                                                || (toggle.Type == "ONLINE" && props.IsOnlineAuctionSearch)
                                                || (toggle.Type == "ONSITE" && props.IsOnSiteAuctionSearch)) ? "active" : "")
                                            + (props.IsFTISearch && !windowSize.isMobile ? " padding" : "")
                                        }
                                        onClick={() => { props.toggleFTISearch(toggle.Type, toggle.Criteria); }}
                                        aria-label={toggle.AriaLabel}
                                        title={toggle.AriaLabel}
                                    >
                                        {toggle.IconUrl && <img fetchpriority='low' src={toggle.IconUrl} className={toggle.IconClass} alt={toggle.IconAltText}></img>}
                                        {toggle.Label}
                                    </div>
                                )
                            })}
                        </div>}
                    {!windowSize.isMobile && props.IsListingsPage &&
                        <ListingOptions
                            setShowAllSpecs={props.setShowAllSpecs}
                            setExpandAllDescriptions={props.setExpandAllDescriptions}
                            showAllSpecs={props.showAllSpecs}
                            showFullDescription={props.showFullDescription}
                            IsAuthenticated={currentUser.IsAuthenticated}
                        />
                    }
                    {(props.SortOrderItems && props.SortOrderItems.length > 0) && !windowSize.isMobile &&
                        <SortDropdown
                            IsDesktop={props.IsDesktop}
                            SortOptions={props.SortOrderItems}
                            OnChange={props.OnChange}
                            SelectedValue={props.SelectedValue}
                            SortButton={props.SortButton}
                            ShowMobileSort={props.ShowMobileSort}
                            toggleMobileSort={props.toggleMobileSort}
                            {...(props.setShowDistanceModal && { setShowDistanceModal: props.setShowDistanceModal })}
                        />}
                </div>
            </div>
        </div>
    );
};

export default ShowClosestFirstToggle;